import {
  TREE_GROUP_BY_CROP,
  TREE_GROUP_BY_FARM,
  TREE_UNCROPPED_LABEL,
  TREE_YEAR_ALL,
} from 'constants/uiConstants';

import { IFarm } from '../interfaces/IFarm';
import { IInput, ZoneLinkType } from '../interfaces/IInput';
import { IPaddock } from '../interfaces/IPaddock';

/** Calendar year the tree is filtered to, or TREE_YEAR_ALL for no filter. */
export type TreeYearFilter = number | typeof TREE_YEAR_ALL;

/** Which hierarchy the tree groups plans into. */
export type TreeGroupBy = typeof TREE_GROUP_BY_FARM | typeof TREE_GROUP_BY_CROP;

export const formatDocName = (docName: string | null): string => {
  if (!docName) return 'Unnamed Input';

  const timestampPattern = /\s\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
  return docName.replace(timestampPattern, '');
};

export const getInputDisplayName = (input: IInput): string => {
  const cropName = input.crop?.cropName?.trim();
  if (cropName) return cropName;

  const docName = input.docName?.trim();
  if (docName) return formatDocName(docName);

  return 'Unnamed Input';
};

const compareNames = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

// The calendar year a plan belongs to, read straight off the ISO string.
//
// Deliberately not `getYearFromDate` (UtilityMethods): that falls back to the
// *current* year when the date is missing, and reads a local-timezone year off
// values written as UTC midnight — so 2025-01-01T00:00:00.000Z resolves to 2024
// anywhere west of UTC. Slicing the string is timezone-independent, and null
// stays null so undated plans can be handled explicitly.
export const getInputYear = (input: IInput): number | null => {
  const startDate = input.startDate;
  if (!startDate) return null;

  // Anchored 4-digit match rather than a bare slice: a malformed value such as
  // ' 2025-01-01' would otherwise slice to ' 202' and bucket the plan under
  // year 202.
  const match = /^\d{4}/.exec(startDate);
  return match ? Number(match[0]) : null;
};

// The plans linked to one specific paddock, out of an org-wide input list —
// shared by the paddock auto-select-plan logic (useFarmsNavSync) and the
// Planning-tab multi-plan picker (OrganisationWorkspace), so both agree on
// what "this paddock's plans" means.
export const getInputsForPaddock = (
  inputs: IInput[],
  paddockId: string
): IInput[] =>
  inputs.filter((input) =>
    input.zone?.links?.some(
      (link) => link.linkType === ZoneLinkType.Paddock && link.linkId === paddockId
    )
  );

/**
 * Every plan grouped under each paddock it links to, in one pass. Use this
 * instead of calling getInputsForPaddock once per paddock when walking a
 * whole farm or organisation — that rescans every plan for every paddock.
 * Same membership rule as getInputsForPaddock: a plan appears once per
 * paddock however many times it links there.
 */
export const indexInputsByPaddock = (
  inputs: IInput[]
): Map<string, IInput[]> => {
  const byPaddock = new Map<string, IInput[]>();

  inputs.forEach((input) => {
    const paddockIds = new Set(
      (input.zone?.links ?? [])
        .filter((link) => link.linkType === ZoneLinkType.Paddock)
        .map((link) => link.linkId)
    );
    paddockIds.forEach((paddockId) => {
      const existing = byPaddock.get(paddockId);
      if (existing) {
        existing.push(input);
      } else {
        byPaddock.set(paddockId, [input]);
      }
    });
  });

  return byPaddock;
};

export const filterInputsByYear = (
  inputs: IInput[],
  year: TreeYearFilter
): IInput[] => {
  if (year === TREE_YEAR_ALL) return inputs;

  // Undated plans survive every year selection — filtering them out would make
  // them unreachable in the tree, since no year would ever show them.
  return inputs.filter((input) => {
    const inputYear = getInputYear(input);
    return inputYear === null || inputYear === year;
  });
};

// The tree's `?year=` URL param, resolved to the filter the reducer holds.
// Shared by useFarmsNavSync and the Farm Plan Preview route so a year in a URL
// always means the same thing.
//
// parseInt's leading-digit tolerance is deliberate, not an oversight: '2026abc'
// resolves to 2026 rather than being rejected, which keeps a slightly mangled
// bookmark working. Anything with no leading digits parses to NaN, fails the
// range check, and falls back to the current year — the same default as a
// missing param, matching the reducer's initial state.
export const parseTreeYearFilter = (raw: string | null): TreeYearFilter => {
  if (raw === TREE_YEAR_ALL) return TREE_YEAR_ALL;

  if (raw !== null) {
    const parsed = parseInt(raw, 10);
    return parsed >= 1900 && parsed <= 2999 ? parsed : new Date().getFullYear();
  }

  return new Date().getFullYear();
};

// Parsed rather than string-compared: dates written by the web app are UTC
// midnight, but mobile-created ones can carry a real time and offset, so
// lexicographic ordering of the raw strings is not reliable.
const getStartTime = (input: IInput): number | null => {
  if (!input.startDate) return null;

  const parsed = Date.parse(input.startDate);
  return Number.isNaN(parsed) ? null : parsed;
};

const sortInputsByStartDate = (inputs: IInput[]) => {
  const decorated = inputs.map((input) => ({
    input,
    startTime: getStartTime(input),
    displayName: getInputDisplayName(input),
  }));

  decorated.sort((a, b) => {
    // Undated plans sink to the bottom instead of sorting as epoch zero.
    if (a.startTime === null && b.startTime === null) {
      return compareNames(a.displayName, b.displayName);
    }
    if (a.startTime === null) return 1;
    if (b.startTime === null) return -1;

    if (a.startTime !== b.startTime) return a.startTime - b.startTime;
    return compareNames(a.displayName, b.displayName);
  });

  for (let i = 0; i < inputs.length; i++) {
    inputs[i] = decorated[i].input;
  }
};

// The non-mutating sibling of sortInputsByStartDate above, for callers holding
// React Query cache data — sorting that in place would mutate the cache.
//
// The two are kept separate on purpose. This one breaks a tie on `input.id`,
// where the private one breaks it on display name: without a stable final key
// two plans sharing a start date can swap places between refetches, which is
// the same reason sortInputsForCropMode ends on `id`. Do not "deduplicate"
// them by pointing the private one here — that would change the order the
// sidebar tree renders plans in.
export const sortInputsByStartDateAscending = (inputs: IInput[]): IInput[] =>
  inputs
    .map((input) => ({ input, startTime: getStartTime(input) }))
    .sort((a, b) => {
      // Undated plans sink to the bottom instead of sorting as epoch zero.
      if (a.startTime === null && b.startTime === null) {
        return a.input.id.localeCompare(b.input.id);
      }
      if (a.startTime === null) return 1;
      if (b.startTime === null) return -1;

      if (a.startTime !== b.startTime) return a.startTime - b.startTime;
      return a.input.id.localeCompare(b.input.id);
    })
    .map((decorated) => decorated.input);

export const getInputVarietyName = (input: IInput): string | null =>
  input.crop?.variety?.varietyName?.trim() || null;

/** paddockId → paddockName across every farm, built once per render pass. */
export const buildPaddockNameMap = (farms: IFarm[]): Map<string, string> => {
  const names = new Map<string, string>();
  for (const farm of farms) {
    for (const paddock of farm.paddocks) {
      names.set(paddock.paddockId, paddock.paddockName);
    }
  }
  return names;
};

// Resolved from the paddock link's id against the farms cache, because
// `snapLinkName` is nullable on ILink and is genuinely absent on documents
// whose zone carried no paddock link at creation time. The snapshot is only a
// fallback for a paddock the cache no longer knows about.
export const resolvePaddockName = (
  input: IInput,
  paddockNames: Map<string, string>
): string | null => {
  const paddockLink = (input.zone?.links ?? []).find(
    (link) => link.linkType === ZoneLinkType.Paddock
  );
  if (!paddockLink) return null;

  // `|| null` rather than `?? null`: a whitespace-only snapshot trims to an
  // empty string, which the `string | null` contract says should be null.
  return (
    paddockNames.get(paddockLink.linkId) ||
    paddockLink.snapLinkName?.trim() ||
    null
  );
};

// The leading space keeps the no-crop bucket out of the crop-name namespace:
// crop names are trimmed before lowercasing, so none can ever start with one.
// Using the display label as the key instead would collide with a crop
// genuinely named "Uncropped": the buckets would merge and one crop's plans
// would disappear from the tree.
const UNCROPPED_KEY = ' uncropped';

export interface CropGroup {
  /** Stable, collision-free key for the tree node. */
  cropKey: string;
  /** Display name — the first spelling seen for this crop. */
  cropName: string;
  inputs: IInput[];
}

// Grouped by crop name, deduped case-insensitively so "Canola" and "canola"
// share one node. The key is produced here rather than re-derived from the
// display name by callers, so there is exactly one place a tree key is minted
// and two nodes can never collide.
export const groupInputsByCrop = (inputs: IInput[]): CropGroup[] => {
  const groups = new Map<string, CropGroup>();

  for (const input of inputs) {
    const cropName = input.crop?.cropName?.trim();
    const cropKey = cropName ? cropName.toLowerCase() : UNCROPPED_KEY;

    const existing = groups.get(cropKey);
    if (existing) {
      existing.inputs.push(input);
    } else {
      groups.set(cropKey, {
        cropKey,
        cropName: cropName || TREE_UNCROPPED_LABEL,
        inputs: [input],
      });
    }
  }

  // Alphabetical, with the no-crop bucket forced last however its label sorts.
  return Array.from(groups.values()).sort((a, b) => {
    if (a.cropKey === UNCROPPED_KEY) return 1;
    if (b.cropKey === UNCROPPED_KEY) return -1;
    return compareNames(a.cropName, b.cropName);
  });
};

// Crop mode orders by variety, then by paddock name — deliberately not by date,
// per the sorting rules agreed on OP-2222. Plans with no variety sort last
// within their crop so the named ones group together at the top.
//
// Takes the prebuilt paddock-name map rather than `farms` so the caller builds
// it once for the whole tree. Decorated rather than resolving inside the
// comparator, which would repeat the lookup twice per comparison.
export const sortInputsForCropMode = (
  inputs: IInput[],
  paddockNames: Map<string, string>
): IInput[] =>
  inputs
    .map((input) => ({
      input,
      variety: getInputVarietyName(input),
      paddockName: resolvePaddockName(input, paddockNames) ?? '',
    }))
    .sort((a, b) => {
      if (a.variety === null && b.variety !== null) return 1;
      if (b.variety === null && a.variety !== null) return -1;

      if (a.variety !== null && b.variety !== null) {
        const byVariety = compareNames(a.variety, b.variety);
        if (byVariety !== 0) return byVariety;
      }

      const byPaddock = compareNames(a.paddockName, b.paddockName);
      if (byPaddock !== 0) return byPaddock;

      // Date is excluded by spec, so id is the only stable final tiebreak —
      // without it two plans in the same paddock with the same variety can
      // swap places between refetches.
      return a.input.id.localeCompare(b.input.id);
    })
    .map((decorated) => decorated.input);

// Returns a new array — `farm.paddocks` comes straight from the React Query
// cache, so sorting it in place would mutate cached data.
export const sortPaddocksByName = (paddocks: IPaddock[]): IPaddock[] =>
  [...paddocks].sort((a, b) =>
    compareNames(a.paddockName ?? '', b.paddockName ?? '')
  );

// The farm-level mirror of sortPaddocksByName, and a new array for the same
// reason — the farms list is React Query cache data. `farmName` is typed
// non-nullable, but the `?? ''` matches its sibling and keeps a malformed
// document from throwing inside the comparator.
export const sortFarmsByName = (farms: IFarm[]): IFarm[] =>
  [...farms].sort((a, b) => compareNames(a.farmName ?? '', b.farmName ?? ''));

export interface ITreeEntities {
  farms: IFarm[];
  inputs: IInput[];
}

// Drops excluded farms and paddocks — and every plan linked to one — so the
// sidebar tree never shows them (OP-2384). Plans carry no `isExcluded` of their
// own; a plan is hidden because its farm or paddock is. The plans have to be
// filtered here too, not just the farms: groupInputsByFarmHierarchy re-files a
// plan whose paddock it can't find under its farm or "Other Inputs", so
// removing only the paddock would move its plans rather than hide them.
//
// A plan linked to a paddock or farm the cache doesn't know at all (soft-
// deleted elsewhere) is kept — only a known exclusion hides a plan.
//
// Returns the input arrays untouched when nothing is excluded, so memoised
// consumers keep a stable identity in the common case.
export const excludeHiddenTreeEntities = (
  farms: IFarm[],
  inputs: IInput[]
): ITreeEntities => {
  const excludedFarmIds = new Set<string>();
  const excludedPaddockIds = new Set<string>();

  for (const farm of farms) {
    if (farm.isExcluded) excludedFarmIds.add(farm.id);
    for (const paddock of farm.paddocks) {
      // An excluded farm's paddocks are hidden with it, so a plan linked only
      // to one of them (no farm link) is caught by the paddock check below.
      if (farm.isExcluded || paddock.isExcluded) {
        excludedPaddockIds.add(paddock.paddockId);
      }
    }
  }

  if (excludedFarmIds.size === 0 && excludedPaddockIds.size === 0) {
    return { farms, inputs };
  }

  const visibleFarms = farms
    .filter((farm) => !farm.isExcluded)
    .map((farm) =>
      farm.paddocks.some((paddock) => paddock.isExcluded)
        ? {
            ...farm,
            paddocks: farm.paddocks.filter((paddock) => !paddock.isExcluded),
          }
        : farm
    );

  const visibleInputs = inputs.filter(
    (input) =>
      !(input.zone?.links ?? []).some(
        (link) =>
          (link.linkType === ZoneLinkType.Paddock &&
            excludedPaddockIds.has(link.linkId)) ||
          (link.linkType === ZoneLinkType.Farm &&
            excludedFarmIds.has(link.linkId))
      )
  );

  return { farms: visibleFarms, inputs: visibleInputs };
};

/** Trimmed, lower-cased search term — empty string means "no search". */
export const normaliseTreeSearch = (raw: string): string =>
  raw.trim().toLowerCase();

const includesTerm = (value: string | null | undefined, term: string) =>
  !!value && value.toLowerCase().includes(term);

// Tree search matches a plan on its crop or variety (OP-2384). Paddock-name
// matching is the caller's concern, since farm mode matches the paddock node
// itself while crop mode matches the plan's resolved paddock. `term` must
// already be normalised.
export const inputMatchesTreeSearch = (input: IInput, term: string): boolean =>
  includesTerm(input.crop?.cropName, term) ||
  includesTerm(getInputVarietyName(input), term);

export const paddockMatchesTreeSearch = (
  paddock: IPaddock,
  term: string
): boolean => includesTerm(paddock.paddockName, term);

export interface IGroupedInputs {
  paddockInputs: Map<string, IInput[]>;
  farmInputs: Map<string, IInput[]>;
  otherInputs: IInput[];
}

export const groupInputsByFarmHierarchy = (
  farms: IFarm[],
  inputs: IInput[]
): IGroupedInputs => {
  const paddockInputs = new Map<string, IInput[]>();
  const farmInputs = new Map<string, IInput[]>();
  const otherInputs: IInput[] = [];

  const paddockToFarmMap = new Map<string, string>();
  const farmIds = new Set<string>();

  for (const farm of farms) {
    farmIds.add(farm.id);
    for (const paddock of farm.paddocks) {
      paddockToFarmMap.set(paddock.paddockId, farm.id);
    }
  }

  for (const input of inputs) {
    const links = input.zone?.links ?? [];

    let paddockId: string | null = null;
    let farmId: string | null = null;

    // Both links are collected rather than stopping at the paddock, so a plan
    // whose paddock is gone can still fall back to its farm below.
    for (const link of links) {
      if (link.linkType === ZoneLinkType.Paddock && paddockId === null) {
        paddockId = link.linkId;
      }
      if (link.linkType === ZoneLinkType.Farm && farmId === null) {
        farmId = link.linkId;
      }
    }

    // A link can point at a paddock or farm the farms cache no longer knows
    // about — soft-deleted on another device, and so excluded by
    // Farm/GetByOrgActive. Bucketing the plan there anyway would file it under a node
    // the tree never renders, and it would vanish from the sidebar. Both
    // buckets are therefore membership-checked, so a plan falls through to its
    // farm, or failing that to "Other Inputs", and live data stays reachable.
    if (paddockId && paddockToFarmMap.has(paddockId)) {
      if (!paddockInputs.has(paddockId)) {
        paddockInputs.set(paddockId, []);
      }
      paddockInputs.get(paddockId)?.push(input);
      continue;
    }

    if (farmId && farmIds.has(farmId)) {
      if (!farmInputs.has(farmId)) {
        farmInputs.set(farmId, []);
      }
      farmInputs.get(farmId)?.push(input);
      continue;
    }

    otherInputs.push(input);
  }

  paddockInputs.forEach((value) => sortInputsByStartDate(value));
  farmInputs.forEach((value) => sortInputsByStartDate(value));
  sortInputsByStartDate(otherInputs);

  return {
    paddockInputs,
    farmInputs,
    otherInputs,
  };
};

const deepEqual = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true;
  if (a === null || b === null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;

  const keysA = Object.keys(a as object);
  const keysB = Object.keys(b as object);
  if (keysA.length !== keysB.length) return false;

  return keysA.every((key) =>
    deepEqual(
      (a as Record<string, unknown>)[key],
      (b as Record<string, unknown>)[key]
    )
  );
};

// Compares two Input documents on their header fields only, ignoring tasks
// (which are saved/diffed independently via the per-task debounce path).
export const areInputHeadersEqual = (
  a: IInput | null,
  b: IInput | null
): boolean => {
  if (!a || !b) return a === b;
  return deepEqual({ ...a, tasks: [] }, { ...b, tasks: [] });
};

// Returns the number of header form fields that differ between draft and saved.
// Fields counted: crop species, crop variety, startDate, endDate,
// zone.zoneArea, zone.areaUnit — one point each.
export const countChangedHeaderFields = (
  draft: IInput | null,
  saved: IInput | null
): number => {
  if (!draft || !saved) return 0;

  let count = 0;

  if (
    draft.crop?.cropName !== saved.crop?.cropName ||
    draft.crop?.globalId !== saved.crop?.globalId
  ) {
    count++;
  }

  if (!deepEqual(draft.crop?.variety ?? null, saved.crop?.variety ?? null)) {
    count++;
  }

  if (draft.startDate !== saved.startDate) count++;
  if (draft.endDate !== saved.endDate) count++;

  if (draft.zone?.zoneArea !== saved.zone?.zoneArea) count++;

  if (!deepEqual(draft.zone?.areaUnit ?? null, saved.zone?.areaUnit ?? null)) {
    count++;
  }

  return count;
};
